/*
 * Copyright © 2015-2018 Serenova, LLC. All rights reserved.
 */
import { fromJS } from 'immutable';
import { isInIframe } from 'serenova-js-utils/browser';
import { Iterable } from 'immutable';

// Initial Sub State
export const initialState = fromJS({
  agentId: '',
  userIsAuthed: !isInIframe(),
  permissions: '',
  currentTenantName: '',
  currentTenantId: '',
  tenants: [],
  platformViewOnlyMode: false,
  userName: '',
  userEmail: '',
  sdkLoaded: false,
});

// Actions
export const updateUserPermissions = (tenantId, tenantName, tenantPermissions, userId) => ({
  type: 'UPDATE_USER_PERMISSIONS',
  tenantInfo: {
    tenantId,
    tenantName,
    tenantPermissions,
  },
  userId,
});

// tenants that user belongs to
export const setUserTenants = (tenants) => ({
  type: 'SET_USER_TENANTS',
  tenants,
});

// tenants present in the login response
export function setAccountTenants(tenants) {
  return {
    type: 'SET_ACCOUNT_TENANTS',
    tenants,
  };
}

// tenants list with format for dropdown
export function setTenantsList(tenants) {
  return {
    type: 'SET_TENANTS_LIST',
    tenants,
  };
}

export function setContactLayouts(contactLayouts) {
  return {
    type: 'SET_CONTACT_LAYOUTS',
    contactLayouts,
  };
}

export const togglePlatformViewOnlyMode = () => ({
  type: 'TOGGLE_PLATFORM_VIEW_ONLY_MODE',
});
export const updatePlatformPermissions = (permissions) => ({
  type: 'UPDATE_PLATFORM_PERMISSIONS',
  permissions,
});

export const setSdkLoaded = (isLoaded) => ({
  type: 'SET_SDK_LOADED',
  isLoaded,
});

export const setUserAuthed = (isAuthed) => ({ type: 'SET_USER_AUTH', isAuthed });

export const switchTenant = (tenantId) => ({ type: 'SWITCH_TENANT', tenantId });
export const setAsActivatedTenant = (tenantId, setAsActiveTenant) => ({ type: 'SET_AS_ACTIVE_TENANT', tenantId, setAsActiveTenant });

export const setUserName = (userName) => ({ type: 'SET_USER_NAME', userName });

export const setUserEmail = (userEmail) => ({ type: 'SET_USER_EMAIL', userEmail });

// Reducer
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case 'UPDATE_USER_PERMISSIONS': {
      const { tenantPermissions, tenantId, tenantName } = action.tenantInfo;

      if (!localStorage.getItem('LIVEOPS-PREFERENCE-KEY')) {
        //Update localStorage if tenant info is not set
        localStorage.setItem(
          'LIVEOPS-PREFERENCE-KEY',
          JSON.stringify({
            tenant: action.tenantInfo,
          })
        );
      }

      return state
        .set('platformPermissions', fromJS(action.platformPermissions))
        .set('permissions', fromJS(tenantPermissions))
        .set('currentTenantId', tenantId)
        .set('currentTenantName', tenantName)
        .set('agentId', action.agentId || action.userId)
        .set('tenantRoleId', action.tenantRoleId || state.get('tenantRoleId'));
    }
    case 'UPDATE_PLATFORM_PERMISSIONS': {
      return state.set('platformPermissions', fromJS(action.permissions));
    }
    case 'SET_USER_TENANTS': {
      return state.set('tenants', fromJS(action.tenants));
    }
    case 'SET_ACCOUNT_TENANTS': {
      return state.set('accountTenants', action.tenants);
    }
    case 'SET_TENANTS_LIST': {
      return state.set('tenantsList', action.tenants);
    }
    case 'SET_CONTACT_LAYOUTS': {
      return state.set('contactLayoutsExt', action.contactLayouts);
    }
    case 'SET_USER_AUTH': {
      return state.set('userIsAuthed', action.isAuthed);
    }
    case 'SET_USER_NAME': {
      return state.set('userName', action.userName);
    }
    case 'SET_USER_EMAIL': {
      return state.set('userEmail', action.userEmail);
    }
    case 'TOGGLE_PLATFORM_VIEW_ONLY_MODE': {
      return state.set('platformViewOnlyMode', !state.get('platformViewOnlyMode'));
    }
    case 'SWITCH_TENANT': {
      let tenants = state.get('accountTenants');
      if (tenants && Iterable.isIterable(tenants)) {
        tenants = tenants.toJS();
      }
      const tenant = tenants.find((tenant) => tenant.tenantId === action.tenantId);
      if (tenant) {
        return state
          .set('permissions', tenant.tenantPermissions)
          .set('currentTenantId', tenant.tenantId)
          .set('currentTenantName', tenant.tenantName)
          .set('tenantRoleId', undefined);
      } else {
        return state;
      }
    }
    case 'SET_SDK_LOADED': {
      return state.set('sdkLoaded', action.isLoaded);
    }
    default:
      return state;
  }
}
